import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import cx from 'classnames';
import Input from '../Input';
import Select from '../Select';
import AsyncSelect from '../AsyncSelect';
import styles from './SearchBar.module.scss';
import { useInnerValue } from '../../../articles/hooks/useInnerValue';
import { SearchIcon } from 'app/scripts/icons';

interface ISelectOption {
  label: string;
  value: string;

  [key: string]: any;
}

interface ISearchBarProps {
  autofocus?: boolean;
  className?: string;
  value?: string;
  searchByValue?: string;
  searchByOptions?: ISelectOption[];
  actionButtons?: React.ReactNode;
  optionsLoaders?: {
    [value: string]: (params: {
      inputValue: string;
      searchBy?: string;
      callback?: (options: ISelectOption[]) => void;
    }) => Promise<ISelectOption[]>;
  };
  onSearchByChange?: (searchByValue: string) => void;
  onChange?: (value: string) => void;
  onSelect?: (value: string) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement | HTMLDivElement>) => void;
}

const SearchBar = (props: ISearchBarProps): JSX.Element => {
  const {
    autofocus = false,
    className,
    actionButtons,
    optionsLoaders = {},
    searchByOptions = [],
    value = '',
    searchByValue = searchByOptions[0]?.value,
    onKeyDown = () => {},
    onSearchByChange = () => {},
    onChange = () => {},
    onSelect = () => {}
  } = props;
  const { innerValue, handleChange } = useInnerValue(value, onChange);
  const searchAutocompleteRef = useRef(null);
  const searchInputRef = useRef(null);
  const selectedSearchOption = innerValue ? { label: innerValue, value: innerValue } : null;
  const selectedSearchByOption = searchByOptions.find(({ value }) => value === searchByValue);
  const optionsLoader = useMemo(() => optionsLoaders[searchByValue], [
    optionsLoaders,
    searchByValue
  ]);

  useEffect(() => {
    const focusSearch = () => {
      const searchRef = optionsLoader ? searchAutocompleteRef : searchInputRef;
      searchRef.current.focus();
    };

    if (autofocus) {
      focusSearch();
    }
  }, [searchByValue, autofocus, optionsLoader]);

  const searchPlaceholder = `Type ${selectedSearchByOption.label}...`;

  const loadOptions = (inputValue: string, callback: (options: ISelectOption[]) => void) =>
    optionsLoader({
      inputValue,
      searchBy: searchByValue,
      callback
    });

  const handleSearchByChange = useCallback(
    ({ value }: ISelectOption) => {
      handleChange('');
      onSearchByChange(value);
    },
    [handleChange, onSearchByChange]
  );

  const handleAutocompleteChange = useCallback(
    ({ value }: ISelectOption) => {
      handleChange(value);
      onSelect(value);
    },
    [handleChange, onSelect]
  );

  return (
    <div className={cx(styles.search, className)} data-testid="search-bar">
      {searchByOptions.length && (
        <div className={styles.searchByContainer}>
          <Select
            icon={<SearchIcon className={styles.searchIcon} />}
            isBorderless
            className={styles.searchByOptions}
            value={selectedSearchByOption}
            options={searchByOptions}
            onChange={handleSearchByChange}
          />
        </div>
      )}

      {optionsLoader && (
        <AsyncSelect
          ref={searchAutocompleteRef}
          isBorderless
          value={selectedSearchOption}
          placeholder={searchPlaceholder}
          className={styles.searchAutocomplete}
          loadOptions={loadOptions}
          onChange={handleAutocompleteChange}
          onKeyDown={onKeyDown}
          onInputChange={handleChange}
          onMenuClose={() => handleChange(innerValue)} // HACK: restoring inputValue after menu close, because react-select does not provide a way to not reset inputValue on menu close if no option has been selected.
        />
      )}

      {!optionsLoader && (
        <Input
          data-testid="search-bar-input"
          isBorderless
          value={innerValue}
          ref={searchInputRef}
          className={styles.searchInput}
          placeholder={searchPlaceholder}
          onChange={handleChange}
          onKeyDown={onKeyDown}
        />
      )}

      {actionButtons && <div className={styles.actionButtons}>{actionButtons}</div>}
    </div>
  );
};

export default SearchBar;
